import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Center,
  Flex,
  Button,
  TextInput,
  Text,
  Paper,
  Title,
  Loader,
  Space,
  Box,
  Image,
  Alert
} from '@mantine/core';
import Confetti from 'react-confetti';
import Lottie from 'lottie-react';
import { NotifyError, NotifySuccess } from '../atoms/notifications';

// -- Lottie JSONs:
import dclogoAnimation from '../assets/lottie/dclogo.json';
import stilllogoAnimation from '../assets/lottie/stilllogo.json';
import signalAnimation from '../assets/lottie/signal.json';
import unlockingAnimation from '../assets/lottie/unlocking.json';
import unlockedAnimation from '../assets/lottie/unlocked.json';
import errorAnimation from '../assets/lottie/error.json';
import verifyAnimation from '../assets/lottie/verify.json';
import { CONFIG } from '../atoms/config';

// -- Our local modules
import { locks } from '../atoms/locks';
import { checkUserAuth, confirmVerificationCode, getSite, unlockAgain, unlockDoor } from '../atoms/network';

// Helper: Calculate distance (in meters) using Haversine formula
function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Auth flow states
const AuthFlow = {
  UNKNOWN: 'UNKNOWN',
  NO_LOCK: 'NO_LOCK',
  NO_LOCATION: 'NO_LOCATION',
  INPUT_DETAILS: 'INPUT_DETAILS',
  LOADING: 'LOADING',
  ENTER_OTP: 'ENTER_OTP',
  UNLOCKED: 'UNLOCKED',
  EXPIRED: 'EXPIRED',
  NOT_NEAR_LOCATION: 'NOT_NEAR_LOCATION'
};

const labelLeftAlign = { style: { textAlign: 'left' } };

const RemainingTimeDisplay = React.memo(function RemainingTimeDisplay({ remainingTime }) {
  return (
    <Text size="lg" mt="sm">
      Remaining Time: {remainingTime} seconds
    </Text>
  );
});

const InputDetailsView = React.memo(function InputDetailsView({
  currentLock,
  name,
  phoneNumber,
  errorMsg,
  handleUnlockClick,
  setName,
  setPhoneNumber,
  showAnimatedLogo,
  site,
  lock
}) {
  const backgroundUrl = site?.image_remote_url || null;
  const imageHeight = '220px';
  console.log(lock, 'qweqweqwe')
  return (
    <Container
      key="input-details"
      size={700}
      px="md"
      style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}
    >
      <Flex justify="center">
        <Image
          src={backgroundUrl}
          width="100%"
          height={imageHeight}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
        />
        <Box mt={110} w={200} justify="center">
          <Lottie
            animationData={showAnimatedLogo ? dclogoAnimation : stilllogoAnimation}
            style={{ width: 200, height: 200 }}
            loop={showAnimatedLogo ? false : true}
          />
        </Box>
      </Flex>

      <Center style={{ paddingTop: 12, paddingBottom: 100 }}>
        <Box style={{ textAlign: 'center', width: '100%' }}>
          <Title order={3}>
            Welcome to {site.title}
          </Title>
          <Text size="sm" c="dimmed">
            {lock.title}
          </Text>
          <Space h={28} />
          <Box sx={{ maxWidth: 320, mx: 'auto', textAlign: 'left' }}>
            <TextInput
              placeholder="Enter Name (Optional)"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              mb="sm"
              size="md"
              labelProps={labelLeftAlign}
            />
            {/* No real-time formatting: just store whatever iOS gives. */}
            <TextInput
              placeholder="Enter Phone Number"
              value={phoneNumber}
              size="md"
              type="tel"
              autoComplete="tel"
              onChange={(e) => setPhoneNumber(e.currentTarget.value)}
              mb="md"
              labelProps={labelLeftAlign}
            />
            {errorMsg && (
              <Text color="red" size="sm" mb="md">
                {errorMsg}
              </Text>
            )}
            <Button size="md" fullWidth onClick={handleUnlockClick}>
              Unlock Door
            </Button>
          </Box>
          <Space h="md" />
          <Text size="xs" sx={{ color: '#999' }}>
            By continuing, you agree to our{' '}
            <a href="https://www.autech.com.au/devonport-privacy">Privacy Policy</a> and{' '}
            <a href="https://www.autech.com.au/devonport-tc">T&Cs</a>.
          </Text>
        </Box>
      </Center>
    </Container>
  );
});

const EnterOtpView = React.memo(function EnterOtpView({ onConfirmOTP, verificationCodeErr }) {
  const [verificationCode, setVerificationCode] = useState('');

  function handleConfirmClick() {
    onConfirmOTP(verificationCode);
  }

  return (
    <Container size={700} px="md" style={{ minHeight: '100vh' }}>
      <Center style={{ height: '100%' }}>
        <Flex direction="column" w="100%">
          <Center>
            <Lottie
              animationData={verifyAnimation}
              style={{ width: 200, height: 230, marginTop: 16 }}
              loop={false}
            />
          </Center>
          <Center>
            <Title order={3} align="center">
              Enter Verification Code
            </Title>
          </Center>
          <Space h="md" />
          <TextInput
            label="Verification Code"
            placeholder="123456"
            size="md"
            type="tel"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.currentTarget.value)}
            labelProps={labelLeftAlign}
          />
          {verificationCodeErr && (
            <Text color="red" mt="md">
              {verificationCodeErr}
            </Text>
          )}
          <Space h="md" />
          <Button size="md" fullWidth onClick={handleConfirmClick}>
            Confirm Code
          </Button>
        </Flex>
      </Center>
    </Container>
  );
});

const UnlockedLottie = React.memo(function UnlockedLottie() {
  const styleRef = useRef({ width: 150, height: 150 });
  return <Lottie animationData={unlockedAnimation} style={styleRef.current} loop={false} />;
});

function UnlockedView({ remainingTime, showConfetti, lockId, name, phone }) {
  const [unlockingAgain, setUnlockingAgain] = useState(false);

  const unlockedLottieEl = useMemo(() => <UnlockedLottie />, []);

  async function handleUnlockAgain() {
    setUnlockingAgain(true);
    try {
      const unlockRes = await unlockAgain(lockId, name, phone);
      if (unlockRes.status) {
        NotifySuccess({
          title: 'Unlocked Successfully',
          message: 'Door has been unlocked again successfully',
        });
      } else {
        NotifyError({
          title: 'Unlock Failed',
          message: 'Could not unlock again. Please try again.',
        });
      }
    } catch (err) {
      NotifyError({
        title: 'Unlock Failed',
        message: `An error occurred: ${err.message}`,
      });
    } finally {
      setUnlockingAgain(false);
    }
  }

  return (
    <Container size={700} px="md">
      {showConfetti && <Confetti recycle={false} />}
      <Center>
        <Box style={{ textAlign: 'center', zIndex: 1, alignItems: 'center' }}>
          <Flex justify="center" mt={224}>
            {unlockedLottieEl}
          </Flex>
          <Title order="2" mt="md">
            Door Unlocked
          </Title>
          <RemainingTimeDisplay remainingTime={remainingTime} />
          <Paper
            shadow="xs"
            p="md"
            mt="md"
            sx={{ maxWidth: 320, mx: 'auto', textAlign: 'left' }}
          >
            <Text size="sm" mb="sm">
              You now have temporary access to the facility. This access is valid for a limited
              time. For assistance, call Council.
            </Text>
            <Flex gap={10}>
              {/* Updated Button: disabled & shows spinner while unlockingAgain is true */}
              <Button
                fullWidth
                onClick={handleUnlockAgain}
                disabled={unlockingAgain}
              >
                {unlockingAgain ? <Loader size="sm" color="white" /> : 'Unlock Again'}
              </Button>

              <Button variant="outline" fullWidth onClick={() => window.open('tel://0364240511')}>
                Call Council
              </Button>
            </Flex>
          </Paper>
        </Box>
      </Center>
    </Container>
  );
}


export default function SmartLockPage() {
  const { lockId } = useParams();

  const [authFlow, setAuthFlow] = useState(AuthFlow.UNKNOWN);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [verificationCodeErr, setVerificationCodeErr] = useState('');
  const [remainingTime, setRemainingTime] = useState(300);
  const [timerId, setTimerId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentLock, setCurrentLock] = useState(null);
  const [showAnimatedLogo, setShowAnimatedLogo] = useState(false);
  const [site, setSite] = useState({});
  const [lock, setLock] = useState({});
  const [invalidLock, setInvalidLock] = useState(false);

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  useEffect(() => {
    if (!lockId) {
      setAuthFlow(AuthFlow.NO_LOCK);
      return;
    }
    else{
      // requestLocationCheck(lockId);
      getSiteData();
    }
  }, [lockId]);

  useEffect(() => {
    if(Object.values(site).length > 0){
      requestLocationCheck();
    }
  }, [site]);

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED) {
      if (timerId) clearInterval(timerId);
      const id = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(id);
            setAuthFlow(AuthFlow.EXPIRED);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setTimerId(id);
      return () => clearInterval(id);
    }
  }, [authFlow]);

  useEffect(() => {
    if (authFlow === AuthFlow.INPUT_DETAILS) {
      const logoTimer = setTimeout(() => {
        setShowAnimatedLogo(true);
      }, 1300);
      return () => clearTimeout(logoTimer);
    } else {
      setShowAnimatedLogo(false);
    }
  }, [authFlow]);

  async function getSiteData(){
      const response = await getSite(lockId);
      console.log(response, 'qjwehjkqwhekjwq')
      if (!response.data) {
        console.log('nrah?')
        return;
      }
      if(response.status){
        if (response.data) {
          setSite(response.data.site);
          setLock(response.data.lock);
        } 
      }
      else{
        setAuthFlow(AuthFlow.NO_LOCK);
        setErrorMsg(`${response.message}`);
        setInvalidLock(true);
      }
  }

  function requestLocationCheck() {
    if (CONFIG.devMode) {
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      return;
    }
    navigator.geolocation.getCurrentPosition(
       (position) => {
        const { latitude, longitude } = position.coords;
        const dist = getDistanceFromLatLonInM(
          latitude,
          longitude,
          site.lat,
          site.lng
        );
        console.log(dist, 'skjaskdl')
        if (dist <= 250) {
          setAuthFlow(AuthFlow.INPUT_DETAILS);
        } else {
          setAuthFlow(AuthFlow.NOT_NEAR_LOCATION);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setAuthFlow(AuthFlow.NO_LOCATION);
      },
      { enableHighAccuracy: true }
    );
  }

  async function handleUnlockClick() {
    // Only require phone number now
    if (!phoneNumber) {
      setErrorMsg('Phone number is required.');
      return;
    }
    setErrorMsg('');
    setAuthFlow(AuthFlow.LOADING);

    // Use "Opt Out" if name is empty
    const submittedName = name.trim() === '' ? 'Opt Out' : name;

    try {
      const response = await checkUserAuth(lockId, submittedName, phoneNumber, '');
      if (!response.data) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);
        setErrorMsg('Unexpected response from server.');
        return;
      }
      if (response.data.auth) {
        setAuthFlow(AuthFlow.UNLOCKED);
      } else {
        setAuthFlow(AuthFlow.ENTER_OTP);
      }
    } catch (err) {
      console.error(err);
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      setErrorMsg(`Failed to authenticate: ${err.message}`);
    }
  }

  async function handleConfirmOTP(codeEnteredByUser) {
    if (!codeEnteredByUser) {
      setVerificationCodeErr('Verification code is required.');
      setAuthFlow(AuthFlow.ENTER_OTP);
      return;
    }
    setVerificationCodeErr('');
    setAuthFlow(AuthFlow.LOADING);

    try {
      const compareRes = await confirmVerificationCode(
        lockId,
        name,
        phoneNumber,
        codeEnteredByUser
      );
      if (!compareRes.data || compareRes.data.auth !== true) {
        setVerificationCodeErr('Invalid verification code.');
        setAuthFlow(AuthFlow.ENTER_OTP);
        return;
      }
      else{
        setShowConfetti(true);
        setAuthFlow(AuthFlow.UNLOCKED);
      }
    } catch (err) {
      console.error(err);
      setVerificationCodeErr(`Verification error: ${err.message}`);
      setAuthFlow(AuthFlow.ENTER_OTP);
    }
  }

  function NoLockView({title = "No App Clip Scanned", message = "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access."}) {
    return (
      <Container size={700} px="md" style={{ height: '100vh' }}>
        <Center style={{ height: '100%' }}>
          <Flex direction="column" align="center" justify="center">
            <Lottie animationData={errorAnimation} style={{ width: 200, height: 200 }} loop={false}/>
            <Title>{title}</Title>
            <Text align="center" style={{ maxWidth: 300 }}>
              {message}
            </Text>
          </Flex>
        </Center>
      </Container>
    );
  }

  function NoLocationView() {
    return (
      <Container size={700} px="md" style={{ height: '100vh' }}>
        <Center style={{ height: '100%' }}>
          <Flex direction="column" align="center" justify="center">
            <Lottie animationData={errorAnimation} style={{ width: 200, height: 200 }} loop={false}/>
            <Title style={{ textAlign: 'center' }}>Location Not Confirmed</Title>
            <Text align="center" style={{ maxWidth: 300 }}>
              We could not verify your location. Please try again or check your location settings.
            </Text>
          </Flex>
        </Center>
      </Container>
    );
  }

  function NotNearLocationView() {
    return (
      <Container size={700} px="md" style={{ height: '100vh' }}>
        <Center style={{ height: '100%' }}>
          <Flex direction="column" align="center" justify="center">
            <Lottie animationData={errorAnimation} style={{ width: 200, height: 200 }} loop={false}/>
            <Title style={{ textAlign: 'center' }}>Invalid Location</Title>
            <Text align="center" style={{ maxWidth: 300 }}>
              We found that you are not near the location of the lock, please try again later.
            </Text>
          </Flex>
        </Center>
      </Container>
    );
  }

  function ExpiredView() {
    return (
      <Container
        size={700}
        px="md"
        style={{ height: '100vh', background: 'linear-gradient(white, #f2f2f2)' }}
      >
        <Center style={{ height: '100%' }}>
          <Flex direction="column" align="center" justify="center">
            <Lottie animationData={errorAnimation} style={{ width: 200, height: 200 }} loop={false} />
            <Title>Access Expired</Title>
            <Text align="center" style={{ maxWidth: 300, marginTop: 8 }}>
              Your temporary access period has ended. If you need access again, scan the QR code or call Council.
            </Text>
          </Flex>
        </Center>
      </Container>
    );
  }

  function Alert({title, text, color}) {
    return (
      <Alert variant="light" color={color} title={title}>
        {text}
      </Alert>
    );
  }

  function LoadingView() {
    return (
      <Container size={700} px="md" style={{ minHeight: '100vh', position: 'relative' }}>
        <Center style={{ height: '100%', position: 'relative' }}>
          <Flex justify="center" direction="column">
            <Flex
              style={{
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Lottie
                style={{ opacity: 0.5, width: 600, height: 600 }}
                animationData={signalAnimation}
                loop
              />
              <Flex
                style={{
                  position: 'absolute',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Flex direction="column" align="center" w="100%">
                  <Lottie
                    style={{ width: 150, height: 150 }}
                    animationData={unlockingAnimation}
                    loop
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Center>
      </Container>
    );
  }

  let content;
  switch (authFlow) {
    case AuthFlow.NO_LOCK:
      content = <NoLockView title={invalidLock ? 'No Locks Found' : 'No App Clip Scanned'} message={invalidLock ? errorMsg : "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access."}/>;
      break;
    case AuthFlow.NO_LOCATION:
      content = <NoLocationView />;
      break;
    case AuthFlow.EXPIRED:
      content = <ExpiredView />;
      break;
    case AuthFlow.UNKNOWN:
      content = (
        <Container size={700} px="md" style={{ height: '100vh' }}>
          <Center style={{ height: '100%' }}>
            <Loader />
          </Center>
        </Container>
      );
      break;
    case AuthFlow.LOADING:
      content = <LoadingView />;
      break;
    case AuthFlow.NOT_NEAR_LOCATION:
      content = <NotNearLocationView />;
      break;
    case AuthFlow.INPUT_DETAILS:
      content = (
        <InputDetailsView
          currentLock={currentLock}
          name={name}
          phoneNumber={phoneNumber}
          errorMsg={errorMsg}
          handleUnlockClick={handleUnlockClick}
          setName={setName}
          setPhoneNumber={setPhoneNumber}
          showAnimatedLogo={showAnimatedLogo}
          site={site}
          lock={lock}
        />
      );
      break;
    case AuthFlow.ENTER_OTP:
      content = (
        <EnterOtpView
          onConfirmOTP={handleConfirmOTP}
          verificationCodeErr={verificationCodeErr}
        />
      );
      break;
    case AuthFlow.UNLOCKED:
      content = <UnlockedView remainingTime={remainingTime} name={name} phone={phoneNumber} lockId={lockId}/>;
      break;
    default:
      content = (
        <Container size={700} px="md">
          <Center style={{ height: '100vh' }}>
            <Text>Unhandled state: {authFlow}</Text>
          </Center>
        </Container>
      );
      break;
  }

  return <>{content}</>;
}
